import { audioEngine } from './audioEngine';
import { logger } from '../utils/logger';

/**
 * Registers the service worker and decides when a new version is allowed to take
 * over.
 *
 * The deciding is the whole point. A new worker installs and then *waits*, which
 * is the safe default: taking over a page that is already running means the old
 * index.html starts asking a new deployment for route chunks that no longer
 * exist, and a lazily-loaded page (the map, explore, analytics) 404s into a blank
 * screen. So the update is applied only on a fresh load, and only when nobody is
 * listening.
 *
 * People leave a radio app playing for hours. Reloading underneath them would
 * cut the audio, so if a stream is running the update simply waits — through as
 * many sessions as it takes — and lands the moment playback stops or the app is
 * next opened.
 */

const SW_URL = '/sw.js';
/** Long enough not to churn, short enough that an update lands the same day. */
const UPDATE_CHECK_INTERVAL = 60 * 60 * 1000;

let reloading = false;

const isPlaying = () => {
  try {
    return audioEngine.getState().isPlaying;
  } catch {
    return false;
  }
};

/**
 * Hand over to the waiting worker, but only with nothing playing.
 * Returns whether it went ahead, so callers can retry later.
 */
const applyUpdate = (registration: ServiceWorkerRegistration): boolean => {
  const waiting = registration.waiting;
  if (!waiting || isPlaying()) return false;

  logger.info('Applying app update');
  waiting.postMessage({ type: 'SKIP_WAITING' });
  return true;
};

export function registerServiceWorker(): void {
  // Never in development: a worker between Vite and the browser makes every
  // change look like it did not take.
  if (!import.meta.env.PROD) return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(SW_URL)
      .then((registration) => {
        // Whether this page was already under a worker's control when it loaded.
        //
        // On a first visit there is no controller, and the worker calling
        // clients.claim() during activation fires controllerchange anyway. Acting
        // on that reloads a brand new visitor for nothing — a visible flash on
        // first load, and an interrupted stream if they were quick to press play.
        // Only a genuine handover from one worker to another needs a reload.
        const hadController = !!navigator.serviceWorker.controller;

        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (!hadController || reloading) return;
          reloading = true;
          window.location.reload();
        });

        const considerUpdate = () => applyUpdate(registration);

        // Something is already waiting from a previous visit.
        if (registration.waiting) considerUpdate();

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            // `controller` distinguishes an update from the very first install,
            // where there is nothing to replace and nothing to reload for.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              logger.info('A new version is ready');
              considerUpdate();
            }
          });
        });

        // Retry the moments an update becomes safe or newly available: the
        // listener stopping, the app coming back into view, and a periodic check
        // for anyone who leaves it open for days.
        audioEngine.subscribe(() => { if (registration.waiting) considerUpdate(); });
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState !== 'visible') return;
          if (registration.waiting) considerUpdate();
          else void registration.update().catch(() => {});
        });
        window.setInterval(() => void registration.update().catch(() => {}), UPDATE_CHECK_INTERVAL);
      })
      .catch((error) => {
        // Offline support is a bonus; the app works without it.
        logger.warning(`Service worker registration failed: ${String(error)}`);
      });
  });
}

/**
 * Escape hatch. Clears every cache, removes the worker and reloads.
 *
 * Exposed on window so it can be run from a console on a device that is stuck,
 * without shipping a new build first — the one thing you cannot do once a bad
 * worker is in the wild is push a fix to someone who is being served the old one.
 */
export function installServiceWorkerKillSwitch(): void {
  if (typeof window === 'undefined') return;
  (window as any).__aurafyResetApp = async () => {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
    window.location.reload();
  };
}
